import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
    DaSelect,
    ButtonGroup,
    DaButton,
    HeartIcon,
} from '@paygreen/paygreen-ui';
import { toast } from 'react-toastify';
import { LineKeyValue, StatusDisplayTable } from '../../../components';
import { apiAuth } from '../../../utils/hooks';
import {
    getDataFromObject,
    timestampToDateWithHour,
} from '../../../utils/tools';
import { useAPIError } from '../../../context';
import useTeamMembers from '../hooks/useTeamMembers';
import {
    shortcutName,
    activationStatusOptions,
    accountCharityManagementList,
    accountManagementList,
    CardInfo,
} from '../shared';
import CharityStripeAccountStatus from '../adminCharityDetails/charityStripeAccount/CharityStripeAccountStatus';

const AccountManagementCard = ({
    t,
    isLoading,
    accountData,
    setIsLoading,
    setAccountData,
    isShop,
    refresh,
}) => {
    const { addError } = useAPIError();
    const { teamSales, teamCSMs, teamAMs } = useTeamMembers();
    const object = isShop ? 'administrator' : 'representative';

    const accountId = accountData?.[object].accountId;
    const donationsId = isShop ? null : accountData?.association.idAssociation;
    const hasCharityDonations = accountData?.association?.validatedAt !== '0';

    const list = isShop ? accountManagementList : accountCharityManagementList;

    const noOneValueForSelect = [
        {
            value: '0',
            text: t('admin.user.accountManagementInfos.noOne'),
        },
    ];

    /**
     * @param {object} tm
     * @returns {object} formatted value to be used in select
     */
    const selectTeamMap = (tm) => {
        return {
            value: tm.idAccountTeam + '',
            text: shortcutName(tm.displayName),
        };
    };

    /**
     *
     * @param {object} accountTeam
     */
    const updateManager = (accountTeam) => {
        setIsLoading(true);
        apiAuth()
            .patch(`/account/${accountId}`, accountTeam)
            .then((res) => {
                if (res.status === 200) {
                    toast(t('form.toastSuccess.accountManagementInfos'));
                    isShop &&
                        setAccountData({ ...accountData, ...accountTeam });
                    !isShop &&
                        setAccountData({
                            ...accountData,
                            accountManager: accountTeam.idAccountManager,
                        });
                }
            })
            .catch((error) =>
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                ),
            )
            .finally(() => setIsLoading(false));
    };

    const CardButton = () => (
        <ButtonGroup align="center" marginTop="sm">
            <RouterLink
                to={{
                    pathname: `/admin/charities/${donationsId}/charity-donations`,
                }}
            >
                <DaButton
                    buttonStyle="line"
                    gradient="theme"
                    colorTheme="quaternary"
                    buttonSize="sm"
                    icon={<HeartIcon />}
                >
                    {t('shopDonations.title')}
                </DaButton>
            </RouterLink>
        </ButtonGroup>
    );

    const handleSalesManagerChange = (e) => {
        if (e.target.value) {
            updateManager({
                idSales: Number(e.target.value),
            });
        }
    };

    const handleAccountManagerChange = (e) => {
        if (e.target.value) {
            updateManager({
                idAccountManager: Number(e.target.value),
            });
        }
    };

    const handleCsmManagerChange = (e) => {
        if (e.target.value) {
            updateManager({
                idCustomerSuccessManager: Number(e.target.value),
            });
        }
    };

    const accountManager = isShop
        ? accountData?.idAccountManager
        : accountData?.accountManager;

    return (
        <CardInfo
            isLoading={isLoading}
            title={t('admin.user.accountManagementInfos.title')}
            hasInput={true}
            button={!!hasCharityDonations && !isShop ? <CardButton /> : null}
        >
            {list.map((item, index) => (
                <LineKeyValue
                    key={index}
                    hasUppercase={false}
                    keyLabel={t(
                        'admin.user.companyInfos.' + item.name + 'Input',
                    )}
                    keyValue={
                        accountData &&
                        timestampToDateWithHour(
                            getDataFromObject(item.value, accountData),
                        )
                    }
                    textSize="xs"
                >
                    {item.type === 'status' &&
                    getDataFromObject(item.value, accountData) === '0' ? (
                        <StatusDisplayTable
                            status="CREATED"
                            statusOptions={activationStatusOptions}
                            i18TextBase="admin.users.table.activationStatusFilter"
                        />
                    ) : null}
                </LineKeyValue>
            ))}

            <>
                <LineKeyValue
                    hasUppercase={false}
                    keyLabel={t('admin.users.table.accountManager')}
                    keyValue={accountData && teamAMs.length > 0}
                    textSize="xs"
                >
                    <DaSelect
                        fieldSize="sm"
                        onChange={handleAccountManagerChange}
                        options={noOneValueForSelect.concat(
                            teamAMs.map(selectTeamMap),
                        )}
                        value={accountManager || '0'}
                    />
                </LineKeyValue>

                {isShop && (
                    <>
                        <LineKeyValue
                            hasUppercase={false}
                            keyLabel={t('admin.users.table.commercial')}
                            keyValue={accountData && teamSales.length > 0}
                            textSize="xs"
                        >
                            <DaSelect
                                fieldSize="sm"
                                onChange={handleSalesManagerChange}
                                options={noOneValueForSelect.concat(
                                    teamSales.map(selectTeamMap),
                                )}
                                value={accountData?.idSales || '0'}
                            />
                        </LineKeyValue>

                        <LineKeyValue
                            hasUppercase={false}
                            keyLabel={t('admin.users.table.csm')}
                            keyValue={accountData && teamCSMs.length > 0}
                            textSize="xs"
                        >
                            <DaSelect
                                fieldSize="sm"
                                onChange={handleCsmManagerChange}
                                options={noOneValueForSelect.concat(
                                    teamCSMs.map(selectTeamMap),
                                )}
                                value={
                                    accountData?.idCustomerSuccessManager || '0'
                                }
                            />
                        </LineKeyValue>
                    </>
                )}
            </>

            {!isShop && (
                <LineKeyValue
                    hasUppercase={false}
                    keyLabel={t('admin.charities.table.stripeAccount')}
                    keyValue={true}
                    textSize="xs"
                >
                    <CharityStripeAccountStatus
                        accountData={accountData}
                        setAccountData={setAccountData}
                        setIsLoading={setIsLoading}
                        isLoading={isLoading}
                        refresh={refresh}
                    />
                </LineKeyValue>
            )}
        </CardInfo>
    );
};

AccountManagementCard.propTypes = {
    accountData: PropTypes.object,
    setAccountData: PropTypes.func,
    isLoading: PropTypes.bool,
    setIsLoading: PropTypes.func,
    refresh: PropTypes.func,
};

AccountManagementCard.defaultProps = {
    isLoading: false,
};

export default withTranslation('common')(AccountManagementCard);
